import gatewayService from '../../../services/GatewayService';
import tokenStorageService from '../../../services/TokenStorageService';
import * as CONSTANTS from '../../constants';
import router from '../../../routes'
import {convertStringDateToDate} from "../../../services/DateToolService";
import {eventTracker} from "../../../services/GoogleAnalyticsService";

/**
 * Fetch availability data for the supplier.
 *
 * @param commit
 * @returns {Promise<T>}
 */
export const getAvailability = ({commit}) => {
    commit(CONSTANTS.DASHBOARD_AVAILABILITY_PENDING);
    const token = tokenStorageService.get();

    return gatewayService.getAvailability(token).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.DASHBOARD_AVAILABILITY_SUCCESS, res.data.data)
        } else {
            commit(CONSTANTS.DASHBOARD_AVAILABILITY_NO_CONTENT)
        }
    }).catch(() => {
        commit(CONSTANTS.DASHBOARD_AVAILABILITY_FAILURE)
    })
};

/**
 * Fetch the accommodation notification data.
 *
 * @param commit
 * @returns {Promise<T>}
 */
export const getAccommodationNotifications = ({commit}) => {
    commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_PENDING);
    const token = tokenStorageService.get();

    return gatewayService.getAccommodationNotifications(token).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_SUCCESS, res.data.data)
        } else {
            commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_NO_CONTENT)
        }
    }).catch(() => {
        commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_FAILURE)
    })
};

/**
 * Sets the accommodation notification filters.
 *
 * @param commit
 * @param rootState
 * @param payload
 */
export const setSelectedFilters = ({ commit, rootState }, payload) => {
    const user = rootState.auth.user;

    commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_FILTERS_SET, {
        data: payload,
        // Google Analytics
        meta: eventTracker('dashboard', 'notifications', 'notifications filtered by user', user.id)
    });
    commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_PAGE_RESET);
};

/**
 * Resets the page of the accommodation notification list.
 *
 * @param commit
 */
export const accommodationNotificationsPageReset = ({commit}) => {
    commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_PAGE_RESET);
};

/**
 * Increment the page of accommodation notification list.
 *
 * @param commit
 */
export const accommodationNotificationPageIncrement = ({commit}) => {
    commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_PAGE_INCREMENT);
};

/**
 * Decrement the page of accommodation notification list.
 *
 * @param commit
 */
export const accommodationNotificationPageDecrement = ({commit}) => {
    commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_PAGE_DECREMENT);
};

/**
 * Sets the accommodation notification page limit.
 *
 * @param commit
 * @param payload
 */
export const setNotificationLimit = ({commit}, payload) => {
    commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_LIMIT_SET, payload);
};

/**
 * Dismiss a specific notification.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const dismissNotification = ({commit}, payload) => {
    commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_DISMISS_PENDING);
    const token = tokenStorageService.get();

    return gatewayService.dismissNotification(token, payload).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_DISMISS_SUCCESS);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'dismiss.success',
                level: 'success'
            }, {root: true});
        } else {
            commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_DISMISS_NO_CONTENT);
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'dismiss.noContent',
                level: 'warning'
            }, {root: true});
        }
    }).catch(() => {
        commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_DISMISS_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'dismiss.error',
            level: 'danger'
        }, {root: true});
    })
};

/**
 * Sets the availability accommodation id selected.
 *
 * @param commit
 * @param rootState
 * @param payload
 */
export const availabilityAccommodationSet = ({ commit, rootState }, payload) => {
    const user = rootState.auth.user;

    commit(CONSTANTS.DASHBOARD_AVAILABILITY_ACCOMMODATION_SET, {
        data: payload,
        // Google Analytics
        meta: eventTracker('dashboard', 'graph', 'accommodation selected by user', user.id)
    });
};

/**
 * Sets the calendar date and opens the allocation modal for the manage allocation page and redirects.
 *
 * @param commit
 * @param rootState
 * @param payload
 */
export const redirect = ({ commit, rootState }, payload) => {
    const user = rootState.auth.user;

    if (!user.contractManager) {
        // Convert the dates for the calendar
        payload = {...payload, from: convertStringDateToDate(payload.from).format('DD/MM/YYYY'), to: convertStringDateToDate(payload.to).format('DD/MM/YYYY')};
        commit('allocation/ALLOCATION_CALENDAR_DATE_SET', {
            data: payload,
            // Google Analytics
            meta: eventTracker('dashboard', 'graph', 'accommodation selected by user', user.id)
        }, {
            root: true,
        });
        commit('allocation/ALLOCATION_MODAL_TOGGLE', true, {root: true});
    }
    router.push('/dashboard/allocation-management/' + payload.siteId + '/' + payload.accommodationSiteId);
};

/**
 * Get availability for a supplier.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const getAvailabilityBySupplierId = ({ commit }, payload) => {
    commit(CONSTANTS.DASHBOARD_AVAILABILITY_PENDING);
    const token = tokenStorageService.get();

    return gatewayService.getAvailabilityBySupplierId(token, payload).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.DASHBOARD_AVAILABILITY_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.DASHBOARD_AVAILABILITY_NO_CONTENT)
        }
    }).catch(() => {
        commit(CONSTANTS.DASHBOARD_AVAILABILITY_FAILURE)
    })
};

/**
 * Get notifications for a supplier.
 *
 * @param commit
 * @param payload
 * @param state
 * @returns {Promise<T>}
 */
export const getAccommodationNotificationsBySupplierId = ({ commit }, payload) => {
    commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_PENDING);
    const token = tokenStorageService.get();

    return gatewayService.getAccommodationNotificationsBySupplierId(token, payload).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_NO_CONTENT)
        }
    }).catch(() => {
        commit(CONSTANTS.DASHBOARD_NOTIFICATIONS_FAILURE)
    })
};

/**
 * Reset the dashboard state
 * @param commit
 */
export const resetState = ({ commit }) => {
    commit(CONSTANTS.DASHBOARD_RESET_STATE);
};



// WEBPACK FOOTER //
// ./src/store/modules/dashboard/actions.js